import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminLogin, forgotPassword } from "@/lib/api";
import { saveAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  // Only allow safe internal redirects (must start with a single "/").
  const rawReturn = searchParams.get("returnTo") || "";
  const returnTo = /^\/(?!\/)/.test(rawReturn) ? rawReturn : "/admin/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await adminLogin(email, password);
      saveAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user?.first_name || "Admin"}`);
      navigate(returnTo);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid credentials";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    if (!email) {
      toast.info("Enter your email above, then tap Forgot password.");
      return;
    }
    setForgotBusy(true);
    try {
      await forgotPassword(email);
      toast.success("If an account exists for that email, a reset link has been sent.");
    } catch {
      toast.success("If an account exists for that email, a reset link has been sent.");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen rd-bg text-white flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12 relative rd-noise">
        <div className="absolute inset-0 rd-glow pointer-events-none" />
        <div className="relative w-full max-w-md flex flex-col items-center">
          {/* Brand + product identity */}
          <Logo height={40} themeAware />
          <h1
            data-testid="product-title"
            className="mt-7 text-center text-3xl sm:text-4xl font-extrabold tracking-tight"
          >
            TeamBuilder AI
          </h1>
          <p
            data-testid="product-subtitle"
            className="mt-2 text-center text-sm text-white/50 tracking-wide"
          >
            Workforce Intelligence &amp; Operations
          </p>

          {/* Login card */}
          <form
            onSubmit={submit}
            className="mt-8 w-full rounded-2xl border border-white/10 p-8"
            style={{ backgroundColor: "#0F1424" }}
            data-testid="admin-login-form"
          >
            <h2 data-testid="login-heading" className="text-2xl font-bold">
              Welcome back
            </h2>
            <p data-testid="login-subtext" className="mt-1.5 mb-6 text-sm text-white/60">
              Sign in to continue to your workspace.
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-white/80 mb-2 block">Email</Label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@rendeavored.com"
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Password</Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="admin-login-button"
                className="w-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11"
              >
                {busy ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
              </Button>
              <button
                type="button"
                onClick={handleForgot}
                disabled={forgotBusy}
                data-testid="admin-forgot-password-link"
                className="w-full text-center text-sm text-[#66B2FF] hover:text-[#8ac2ff] transition-colors disabled:opacity-50"
              >
                {forgotBusy ? "Sending reset link\u2026" : "Forgot password?"}
              </button>
            </div>
          </form>

          {/* Product descriptor */}
          <div className="mt-7 text-center">
            <p
              data-testid="product-descriptor"
              className="text-sm font-medium tracking-wide text-white/55"
            >
              Sales &bull; Talent &bull; Workforce Planning
            </p>
            <p
              data-testid="product-attribution"
              className="mt-1 text-xs text-white/35"
            >
              TeamBuilder AI by Rendeavored
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
